import {
  Tag, TagLabel, Icon, Circle, Skeleton, TagProps,
} from "@chakra-ui/react";
import CaminoDocumentCheck from "components/Icon/CaminoDocumentCheck";
import { ExternalLinkButton } from "components/Link";
import { useTaskDetailQuery } from "features/Resources/Task/hooks";
import { TaskId } from "features/Resources/Task/models";
import { useTaskTemplateDetailQuery } from "features/Resources/TaskTemplate/hooks";
import { TaskTemplateId } from "features/Resources/TaskTemplate/models";
import { taskPathById, taskTemplatePathById } from "helpers/url/pages/v2/actionPaths";

interface TaskDependencyPillBaseProps {
  name: string | undefined;
  url: string;
  isLoading: boolean;
  tagProps?: TagProps;
}

function TaskDependencyPillBase({ name, url, isLoading, tagProps = {} }: TaskDependencyPillBaseProps) {
  return (
    <Tag size="md" colorScheme="gray" borderRadius="full" {...tagProps}>
      <Circle size="5" bg="cdsOrange.500" color="white" ml="-1">
        <Icon as={CaminoDocumentCheck} boxSize={4} />
      </Circle>
      <TagLabel ml="1" mr=".5">
        <Skeleton isLoaded={!isLoading} minW="80px">
          {name}
          {" "}
          <ExternalLinkButton label="View Task Template" to={url} />
        </Skeleton>
      </TagLabel>
    </Tag>
  );
}

interface TaskTemplateDependencyPillProps {
  taskTemplateId: TaskTemplateId;
}

export function TaskTemplateDependencyPill({ taskTemplateId, ...rest }: TaskTemplateDependencyPillProps) {
  const { data: taskTemplate, isFetching } = useTaskTemplateDetailQuery(taskTemplateId);
  const taskTemplateUrl = taskTemplatePathById(taskTemplateId);
  return <TaskDependencyPillBase name={taskTemplate?.name} url={taskTemplateUrl} isLoading={isFetching} tagProps={rest} />;
}

interface TaskDependencyPillProps extends TagProps {
  taskId: TaskId;
}

export function TaskDependencyPill({ taskId, ...rest }: TaskDependencyPillProps) {
  const { data: taskTemplate, isFetching } = useTaskDetailQuery(taskId);
  const taskUrl = taskPathById(taskId);
  return <TaskDependencyPillBase name={taskTemplate?.name} url={taskUrl} isLoading={isFetching} tagProps={rest} />;
}

interface DependencyPillProps {
  pageType: "TaskTemplate" | "Task";
  dependencyId: TaskTemplateId  ;
}

export function DependencyPill({ pageType, dependencyId }: DependencyPillProps) {
  switch (pageType) {
    case "Task":
      return <TaskDependencyPill taskId={dependencyId} />;
    case "TaskTemplate":
      return <TaskTemplateDependencyPill taskTemplateId={dependencyId} />;
    default:
      return "";
  }
}
